import { createMealPlaceRequestSchema, mealPlaceDtoSchema, mealPlacesResponseSchema, personPreferencesResponseSchema, personProfileRequestSchema, putPersonPreferencesRequestSchema, updateMealPlaceRequestSchema, acceptInviteRequestSchema, adminDisableHouseholdRequestSchema, adminHouseholdsResponseSchema, adminReportsResponseSchema, adminResolveReportRequestSchema, adminResolveReportResponseSchema, auditLogResponseSchema, authConfigResponseSchema, changePasswordRequestSchema, createHouseholdRequestSchema, createInviteRequestSchema, deleteAccountRequestSchema, equipmentResponseSchema, forgotPasswordRequestSchema, healthzResponseSchema, householdMeResponseSchema, householdPeopleResponseSchema, householdPersonDtoSchema, householdPersonRequestSchema, inviteDtoSchema, invitesResponseSchema, loginRequestSchema, membersResponseSchema, meResponseSchema, passkeyLoginOptionsRequestSchema, passkeyLoginVerifyRequestSchema, passkeyRegisterVerifyRequestSchema, passkeysResponseSchema, readyzResponseSchema, registerRequestSchema, renamePasskeyRequestSchema, replaceEquipmentRequestSchema, resetPasswordRequestSchema, sessionsResponseSchema, switchHouseholdRequestSchema, updateHouseholdRequestSchema, updateMemberRoleRequestSchema, updateProfileRequestSchema, verifyEmailRequestSchema, } from '@fewerbags/schema';
import { ApiError, createHttp, NetworkError } from './http.js';
import { createAuthTokensApi } from './auth-tokens.js';
import { createCalendarApi, createRemindersApi, createScheduleApi } from './calendar.js';
import { createGroceriesApi } from './groceries.js';
import { createIngredientsApi } from './ingredients.js';
import { createImportsApi } from './imports.js';
import { createMediaApi } from './media.js';
import { createPantryApi } from './pantry.js';
import { createPlannerApi } from './planner.js';
import { createPlansApi } from './plans.js';
import { createRecipesApi } from './recipes.js';
import { createStoresApi } from './stores.js';
export { ApiError, NetworkError };
/** Validate a request body locally before it leaves the device (same schema the API uses). */
function body(schema, value) {
    return schema.parse(value);
}
const API = '/api/v1';
export function createApiClient(opts) {
    const http = createHttp(opts);
    const a = `${API}/auth`;
    const h = `${API}/households`;
    const adm = `${API}/admin`;
    return {
        recipes: createRecipesApi(http, `${API}/recipes`),
        ingredients: createIngredientsApi(http, `${API}/ingredients`),
        pantry: createPantryApi(http, `${API}/pantry`),
        plans: createPlansApi(http, `${API}/plans`),
        planner: createPlannerApi(http, `${API}/planner`),
        stores: createStoresApi(http, `${API}/stores`),
        groceries: createGroceriesApi(http, `${API}/groceries`),
        media: createMediaApi(http, `${API}/media`),
        imports: createImportsApi(http, `${API}/imports`),
        schedule: createScheduleApi(http, h),
        calendar: createCalendarApi(http, `${API}/calendar`),
        reminders: createRemindersApi(http, API),
        /** Mobile bearer-token auth (Phase 5) — separate from the `auth.*` cookie flow. */
        authTokens: createAuthTokensApi(http, a),
        healthz: async () => http.get('/healthz', { parse: healthzResponseSchema }),
        readyz: async () => http.get('/readyz', { parse: readyzResponseSchema }),
        auth: {
            /** Public sign-up settings: which captcha widget to render (and its site key). */
            config: async () => http.get(`${a}/config`, { parse: authConfigResponseSchema }),
            /** Resolves null when anonymous (401) instead of throwing. */
            me: async () => {
                try {
                    return await http.get(`${a}/me`, { parse: meResponseSchema, silent401: true });
                }
                catch (err) {
                    if (err instanceof ApiError && err.status === 401)
                        return null;
                    throw err;
                }
            },
            register: async (input) => http.post(`${a}/register`, body(registerRequestSchema, input)),
            login: async (input) => http.post(`${a}/login`, body(loginRequestSchema, input), { silent401: true }),
            logout: async () => http.post(`${a}/logout`),
            updateProfile: async (input) => http.patch(`${a}/me`, body(updateProfileRequestSchema, input)),
            verifyEmail: async (input) => http.post(`${a}/verify-email`, body(verifyEmailRequestSchema, input)),
            resendVerification: async () => http.post(`${a}/resend-verification`),
            forgotPassword: async (input) => http.post(`${a}/forgot-password`, body(forgotPasswordRequestSchema, input)),
            resetPassword: async (input) => http.post(`${a}/reset-password`, body(resetPasswordRequestSchema, input)),
            changePassword: async (input) => http.post(`${a}/change-password`, body(changePasswordRequestSchema, input)),
            deleteAccount: async (input) => http.del(`${a}/me`, body(deleteAccountRequestSchema, input)),
            sessions: {
                list: async () => http.get(`${a}/sessions`, { parse: sessionsResponseSchema }),
                revoke: async (id) => http.del(`${a}/sessions/${encodeURIComponent(id)}`),
            },
            passkeys: {
                list: async () => http.get(`${a}/passkeys`, { parse: passkeysResponseSchema }),
                /** Returns PublicKeyCredentialCreationOptionsJSON (validated by @simplewebauthn/browser). */
                registerOptions: async () => http.post(`${a}/passkeys/register/options`, undefined, { parse: (v) => v }),
                registerVerify: async (input) => http.post(`${a}/passkeys/register/verify`, body(passkeyRegisterVerifyRequestSchema, input)),
                rename: async (id, input) => http.patch(`${a}/passkeys/${encodeURIComponent(id)}`, body(renamePasskeyRequestSchema, input)),
                remove: async (id) => http.del(`${a}/passkeys/${encodeURIComponent(id)}`),
                /** Returns PublicKeyCredentialRequestOptionsJSON. */
                loginOptions: async (input = {}) => http.post(`${a}/passkeys/login/options`, body(passkeyLoginOptionsRequestSchema, input), { parse: (v) => v, silent401: true }),
                loginVerify: async (input) => http.post(`${a}/passkeys/login/verify`, body(passkeyLoginVerifyRequestSchema, input), {
                    silent401: true,
                }),
            },
        },
        households: {
            create: async (input) => http.post(h, body(createHouseholdRequestSchema, input), {
                parse: householdMeResponseSchema,
            }),
            me: async () => http.get(`${h}/me`, { parse: householdMeResponseSchema }),
            update: async (input) => http.patch(`${h}/me`, body(updateHouseholdRequestSchema, input)),
            switch: async (input) => http.post(`${h}/switch`, body(switchHouseholdRequestSchema, input), {
                parse: householdMeResponseSchema,
            }),
            join: async (input) => http.post(`${h}/join`, body(acceptInviteRequestSchema, input), {
                parse: householdMeResponseSchema,
            }),
            members: {
                list: async () => http.get(`${h}/me/members`, { parse: membersResponseSchema }),
                updateRole: async (userId, input) => http.patch(`${h}/me/members/${encodeURIComponent(userId)}`, body(updateMemberRoleRequestSchema, input)),
                remove: async (userId) => http.del(`${h}/me/members/${encodeURIComponent(userId)}`),
            },
            /** Everyone who can be rated for (D30); add/rename/remove only people without an account. */
            people: {
                list: async () => http.get(`${h}/me/people`, { parse: householdPeopleResponseSchema }),
                create: async (input) => http.post(`${h}/me/people`, body(householdPersonRequestSchema, input), {
                    parse: householdPersonDtoSchema,
                }),
                rename: async (id, input) => http.patch(`${h}/me/people/${encodeURIComponent(id)}`, body(householdPersonRequestSchema, input), { parse: householdPersonDtoSchema }),
                remove: async (id) => http.del(`${h}/me/people/${encodeURIComponent(id)}`),
                /** Allergies, diets and notes (D31): owners/adults, or the person's own account. */
                updateProfile: async (id, input) => http.patch(`${h}/me/people/${encodeURIComponent(id)}/profile`, body(personProfileRequestSchema, input), { parse: householdPersonDtoSchema }),
                /** Stated likes/dislikes, then learned ones (D31). */
                preferences: async (id) => http.get(`${h}/me/people/${encodeURIComponent(id)}/preferences`, {
                    parse: personPreferencesResponseSchema,
                }),
                /** Replace stated likes/dislikes; confirm or reject learned entries. */
                putPreferences: async (id, input) => http.put(`${h}/me/people/${encodeURIComponent(id)}/preferences`, body(putPersonPreferencesRequestSchema, input), { parse: personPreferencesResponseSchema }),
            },
            /** Where meals happen (D32): home (always present), work, school … */
            mealPlaces: {
                list: async () => http.get(`${h}/me/meal-places`, { parse: mealPlacesResponseSchema }),
                create: async (input) => http.post(`${h}/me/meal-places`, body(createMealPlaceRequestSchema, input), {
                    parse: mealPlaceDtoSchema,
                }),
                update: async (id, input) => http.patch(`${h}/me/meal-places/${encodeURIComponent(id)}`, body(updateMealPlaceRequestSchema, input), { parse: mealPlaceDtoSchema }),
                remove: async (id) => http.del(`${h}/me/meal-places/${encodeURIComponent(id)}`),
            },
            invites: {
                list: async () => http.get(`${h}/me/invites`, { parse: invitesResponseSchema }),
                create: async (input = {}) => http.post(`${h}/me/invites`, body(createInviteRequestSchema, input), {
                    parse: inviteDtoSchema,
                }),
                revoke: async (id) => http.del(`${h}/me/invites/${encodeURIComponent(id)}`),
            },
            equipment: {
                list: async () => http.get(`${h}/me/equipment`, { parse: equipmentResponseSchema }),
                replace: async (input) => http.put(`${h}/me/equipment`, body(replaceEquipmentRequestSchema, input), {
                    parse: equipmentResponseSchema,
                }),
            },
        },
        admin: {
            households: {
                list: (query = {}) => http.get(`${adm}/households`, {
                    parse: adminHouseholdsResponseSchema,
                    query: {
                        q: query.q,
                        includeDisabled: query.includeDisabled === undefined ? undefined : String(query.includeDisabled),
                        limit: query.limit,
                        offset: query.offset,
                    },
                }),
                disable: async (id, input) => http.post(`${adm}/households/${encodeURIComponent(id)}/disable`, body(adminDisableHouseholdRequestSchema, input)),
                enable: async (id) => http.post(`${adm}/households/${encodeURIComponent(id)}/enable`),
                audit: async (id) => http.get(`${adm}/households/${encodeURIComponent(id)}/audit`, {
                    parse: auditLogResponseSchema,
                }),
            },
            audit: async () => http.get(`${adm}/audit`, { parse: auditLogResponseSchema }),
            /** Content reports folded per target; never carries reporter identity (D12). */
            reports: {
                list: async (status = 'open') => http.get(`${adm}/reports`, { parse: adminReportsResponseSchema, query: { status } }),
                resolve: async (id, input) => http.post(`${adm}/reports/${encodeURIComponent(id)}/resolve`, body(adminResolveReportRequestSchema, input), { parse: adminResolveReportResponseSchema }),
            },
        },
    };
}
//# sourceMappingURL=index.js.map